import { useEffect, useRef, useState } from "react";
import { useClerk } from "@clerk/react";
import { toast } from "sonner";
import { Checkbox } from "./ui/checkbox";
import { useApi, errMsg } from "../lib/api";
import { BrandLogo } from "./brand/BrandLogo";

const CodeBoxes = ({ code, setCode }) => {
  const refs = useRef([]);
  const set = (i, v) => {
    const d = v.replace(/\D/g, "");
    if (d.length > 1) { setCode(d.slice(0, 6)); refs.current[Math.min(d.length, 5)]?.focus(); return; }
    const next = code.split(""); next[i] = d; setCode(next.join("").slice(0, 6));
    if (d && i < 5) refs.current[i + 1]?.focus();
  };
  return (
    <div className="grid grid-cols-6 gap-2" data-testid="mfa-code-boxes">{[0, 1, 2, 3, 4, 5].map((i) => (
      <input key={i} ref={(el) => { refs.current[i] = el; }} inputMode="numeric" maxLength={6} value={code[i] || ""} data-testid={`mfa-code-${i}`}
        onChange={(e) => set(i, e.target.value)} onKeyDown={(e) => { if (e.key === "Backspace" && !code[i] && i) refs.current[i - 1]?.focus(); }}
        className="h-14 rounded-xl bg-[#16294A] border border-slate-700 text-center text-2xl font-bold text-white focus:border-[#D7263D] outline-none transition-colors" />))}</div>
  );
};

export function MfaGate({ onDone }) {
  const api = useApi();
  const { signOut } = useClerk();
  const [sent, setSent] = useState(null);
  const [code, setCode] = useState("");
  const [trust, setTrust] = useState(true);
  const [busy, setBusy] = useState(false);
  const sentOnce = useRef(false);
  const send = async () => { try { const r = await api.post("/2fa/send"); setSent(r.data.sent_to); } catch (e) { toast.error(errMsg(e)); } };
  useEffect(() => { if (!sentOnce.current) { sentOnce.current = true; send(); } }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const verify = async () => {
    setBusy(true);
    try { const r = await api.post("/2fa/verify", { code, trust }); localStorage.setItem("bsafe_device", r.data.device_token); onDone(); }
    catch (e) { toast.error(errMsg(e)); setCode(""); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-[60] bg-[#0B1530] flex items-center justify-center p-5" style={{ fontFamily: "Sora, sans-serif" }} data-testid="mfa-gate">
      <div className="w-full max-w-md fade-up">
        <BrandLogo tone="dark" size={34} />
        <h1 className="mt-10 text-3xl font-extrabold text-white">Check your email</h1>
        <p className="mt-3 text-sm text-slate-300" data-testid="mfa-sent-to">We sent a 6-digit code to {sent || "your email"}. Admin logins always need this second step.</p>
        <div className="mt-6"><CodeBoxes code={code} setCode={setCode} /></div>
        <label className="mt-4 flex items-center gap-2 text-sm text-slate-300"><Checkbox data-testid="mfa-trust-checkbox" checked={trust} onCheckedChange={(v) => setTrust(!!v)} />Trust this device for 30 days</label>
        <button data-testid="mfa-verify-btn" disabled={code.length !== 6 || busy} onClick={verify}
          className="mt-5 w-full h-12 rounded-full bg-[#D7263D] text-white font-bold hover:bg-[#c01f34] disabled:opacity-50 transition-colors">{busy ? "Checking…" : "Verify and continue"}</button>
        <div className="mt-4 flex justify-between text-sm">
          <button className="text-slate-400 hover:text-white" data-testid="mfa-signout-btn" onClick={() => signOut({ redirectUrl: "/sign-in" })}>← Back</button>
          <button className="text-[#FF7A8A] hover:underline" data-testid="mfa-resend-btn" onClick={send}>Send a new code</button>
        </div>
        <p className="mt-6 text-xs text-slate-500">WhatsApp and SMS codes will be added once WhatsApp sending is approved.</p>
      </div>
    </div>
  );
}

export function useIdleSignOut(minutes) {
  const { signOut } = useClerk();
  useEffect(() => {
    if (!minutes) return undefined;
    let t;
    const reset = () => { clearTimeout(t); t = setTimeout(() => signOut({ redirectUrl: "/sign-in" }), minutes * 60000); };
    const evs = ["mousemove", "keydown", "touchstart", "scroll"];
    evs.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => { clearTimeout(t); evs.forEach((e) => window.removeEventListener(e, reset)); };
  }, [minutes, signOut]);
}
